import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import useGetFetchConfig from '../useGetFetchConfig';

interface UseDownloadAudioTranscriptPdfProps {
  matterId: string;
  docId: string;
}

const readErrorMessage = (body: string): string | null => {
  try {
    const parsed: unknown = JSON.parse(body);
    const message = (parsed as { message?: unknown }).message;
    return typeof message === 'string' ? message : null;
  } catch {
    return null;
  }
};

export const useDownloadAudioTranscriptPdf = ({ matterId, docId }: UseDownloadAudioTranscriptPdfProps) => {
  const { fetchConfigGET } = useGetFetchConfig();

  const downloadTranscript = async () => {
    const urlResponse = await fetch(
      `${APIBaseChronos}/api/matters/${matterId}/docs/${docId}/audio/transcript-pdf`,
      fetchConfigGET,
    );

    if (!urlResponse.ok) {
      throw new Error(readErrorMessage(await urlResponse.text()) ?? 'Failed to get transcript URL');
    }

    const presignedUrl = await urlResponse.text();

    const link = document.createElement('a');
    link.href = presignedUrl;
    link.target = '_blank';
    link.download = `wexler-transcript-${docId}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return useMutation(downloadTranscript, {
    onError: (error: Error) => {
      // The backend returns a specific message when the document has no transcript yet.
      toast.error(error.message.includes('No transcript') ? error.message : 'Failed to download transcript');
    },
  });
};
